/**
 * Consume a slate hand-off from Home (specs/home-page AC-10, AC-10b) inside `CreatePageProvider`: load
 * the slips, open Create on the slate's day, then clear the hand-off so it runs once. "Send to a Friend"
 * lands on the rival step; "Beat the Monkey" pre-selects the Monkey (clearing any group) and lands on
 * review so one more tap sends it. An effect (not an initial-state read) so it works whether the
 * provider mounts after the tap or is already mounted from an earlier visit to the create tab.
 */

import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { MONKEY_USER_ID } from '../../../constants/monkey';
import { usePendingSlatePrefillStore } from '../../home/stores/pendingSlatePrefillStore';
import type { Group } from '../../../types/challenges';
import type { CreateStep, PickSlip } from '../types/create.types';

export interface UseSlatePrefillConsumerProps {
  setPickSlips: Dispatch<SetStateAction<PickSlip[]>>;
  setStep: Dispatch<SetStateAction<CreateStep>>;
  setSelectedDate: Dispatch<SetStateAction<Date>>;
  setSelectedFriendId: Dispatch<SetStateAction<string | null>>;
  setSelectedGroupId: Dispatch<SetStateAction<string | null>>;
  setSelectedGroup: Dispatch<SetStateAction<Group | null>>;
}

export function useSlatePrefillConsumer({
  setPickSlips,
  setStep,
  setSelectedDate,
  setSelectedFriendId,
  setSelectedGroupId,
  setSelectedGroup,
}: UseSlatePrefillConsumerProps): void {
  const prefill = usePendingSlatePrefillStore((s) => s.prefill);

  useEffect(() => {
    if (!prefill) return;
    setPickSlips(prefill.slips);
    if (prefill.selectedDateIso) setSelectedDate(new Date(prefill.selectedDateIso));
    if (prefill.opponent === 'monkey') {
      setSelectedGroupId(null);
      setSelectedGroup(null);
      setSelectedFriendId(MONKEY_USER_ID);
      setStep('review');
    } else {
      setStep('rival');
    }
    usePendingSlatePrefillStore.getState().clear();
  }, [prefill, setPickSlips, setStep, setSelectedDate, setSelectedFriendId, setSelectedGroupId, setSelectedGroup]);
}
